import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { EquipmentLogbookModel } from 'src/app/models/archive/equipment-logbook.model';
import { MaintenanceStrategyModel } from 'src/app/models/archive/maintenance-strategy.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { MaintenanceStrategyTableModel } from 'src/app/models/tables/archive/maintenance-strategy-table.model';
import { EquipmentLogbookService } from 'src/app/services/archive/equipment-logbook.service';
import { MaintenanceStrategyService } from 'src/app/services/archive/maintenance-strategy.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-maintenance-strategy',
  templateUrl: './maintenance-strategy.component.html',
  styleUrls: ['./maintenance-strategy.component.scss'],
  standalone: false
})

export class MaintenanceStrategyComponent implements OnInit {
  private readonly config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly equipmentLogbookService = inject(EquipmentLogbookService);
  private readonly maintenanceStrategyService = inject(MaintenanceStrategyService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  ascendant: boolean = true;
  articleId: number = 0;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  defaultDayUnitId: number = 0;
  equipmentLogbookModel: EquipmentLogbookModel = new EquipmentLogbookModel();
  formEquipmentLogbook: FormGroup = new FormGroup({});
  formMaintenanceStrategy: FormGroup = new FormGroup({});
  isFormView: boolean = false;
  limit: number = 10;
  listMaintenancePlans: any[] = [];
  listUnits: any[] = [];
  maintenanceStrategyContract: MaintenanceStrategyModel = new MaintenanceStrategyModel();
  maintenanceStrategyData: PagedResponseModel<MaintenanceStrategyTableModel> = new PagedResponseModel<MaintenanceStrategyTableModel>();
  maintenanceStrategyHeader: ColumnResponseModel[] = [];
  page: number = 1;
  suscription: Subscription;
  swMaintenanceScheduling: boolean = false;
  swMaintenanceStrategy: boolean = false;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];

  constructor() {
    this.validateForm(this.equipmentLogbookModel);
    this.validateFormStrategy(this.maintenanceStrategyContract);
    this.getColumnsForTableAsync();
  }

  async ngOnInit() {
    await this.getListUnits();

    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.equipmentLogbookModel = inputs.equipmentLogbookModel;
      this.swMaintenanceStrategy = inputs.swMaintenanceStrategy;
      this.swMaintenanceScheduling = inputs.swMaintenanceScheduling;
    });

    this.validateForm(this.equipmentLogbookModel);
    this.articleId = this.equipmentLogbookModel.articleId;
    const { unitId } = this.equipmentLogbookModel;
    if (unitId != null) {
      await this.getAllMaintenanceStrategiesWithPagination();
    } else {
      const unitControl = this.formEquipmentLogbook.controls['unitId'];
      if (this.defaultDayUnitId) {
        setTimeout(() => {
          unitControl.setValue(this.defaultDayUnitId);
          unitControl.disable();
        });
      }
    }
  }

  async addMaintenanceStrategy() {
    if (this.equipmentLogbookModel.maintenanceFrequency == null) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe definir la frecuencia de mantenimiento.'
      );
      return;
    }

    this.validateFormStrategy(new MaintenanceStrategyModel())
    const response = await firstValueFrom(this.maintenanceStrategyService.getLastMaintenanceStrategy(this.articleId));
    const interval = response.interval + 1
    const frecuency = interval * this.equipmentLogbookModel.maintenanceFrequency!
    this.formMaintenanceStrategy.controls['interval'].patchValue(interval);
    this.formMaintenanceStrategy.controls['frequency'].patchValue(frecuency);
    this.isFormView = true;
  }

  onChangeUnit(unitId: number) {
    if (!this.defaultDayUnitId) return;
    if (unitId !== this.defaultDayUnitId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debido a la configuración de la compañía la unidad de medición debe ser DIA.'
      );
      setTimeout(() =>
        this.formEquipmentLogbook.controls['unitId'].setValue(this.defaultDayUnitId)
      );
    }
    setTimeout(() => this.formEquipmentLogbook.controls['unitId'].disable());
  }

  clearForm() {
    this.isFormView = false;
    this.validateFormStrategy(new MaintenanceStrategyModel());
  }

  async deleteMaintenanceStrategy(selectedIds: number[]) {
    if (selectedIds.length === 0) return;
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar la estrategia mtto.?');
    if (accepted) {
      try {
        await firstValueFrom(this.maintenanceStrategyService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'La estrategia mtto. fue eliminada exitosamente.'
        );
        await this.getAllMaintenanceStrategiesWithPagination();
        this.clearSelectionTrigger++;
        this.maintenanceStrategyContract = new MaintenanceStrategyModel();
        this.validateFormStrategy(this.maintenanceStrategyContract);
        this.isFormView = false;
      } catch (error) {
        console.error('Error al eliminar la estrategia mtto.', error);
      }
    }
  }

  async getAllMaintenanceStrategiesWithPagination() {
    try {
      this.maintenanceStrategyData = await firstValueFrom(
        this.maintenanceStrategyService.getAllMaintenanceStrategiesWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.articleId
        )
      );
    } catch (error) {
      console.error('Error al buscar la estrategia mtto.', error);
    }
  }

  async getColumnsForTableAsync() {
    try {
      if (this.maintenanceStrategyHeader.length === 0) {
        this.maintenanceStrategyHeader = await firstValueFrom(this.maintenanceStrategyService.getColumnsForTableAsync());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta estrategia mtto.', error);
    }
  }

  async getListUnits() {
    this.listUnits = await firstValueFrom(this.maintenanceStrategyService.getAllUnitsForSelect());
    const defaultUnit = this.listUnits.find(x => x.defaultDayUnitId > 0);
    if (defaultUnit) {
      this.defaultDayUnitId = defaultUnit.defaultDayUnitId;
    }
    this.listMaintenancePlans = await firstValueFrom(this.maintenanceStrategyService.getAllMaintenancePlansForSelect());
  }

  async editMaintenanceStrategy(item: MaintenanceStrategyTableModel) {
    try {
      const response = await firstValueFrom(this.maintenanceStrategyService.getMaintenanceStrategyById(item.id));
      if (response) {
        this.maintenanceStrategyContract = response;
        this.validateFormStrategy(this.maintenanceStrategyContract);
        this.isFormView = true;
      }
    } catch (error) {
      console.error('Error al buscar la estrategia mtto.', error);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllMaintenanceStrategiesWithPagination();
  }

  async onAction(event: { action: string, item?: MaintenanceStrategyTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      await this.editMaintenanceStrategy(event.item);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      await this.deleteMaintenanceStrategy(event.selectedItems);
    }
  }

  async send() {
    if (!this.formMaintenanceStrategy.valid) {
      this.showValidationMessagesStrategy();
      return;
    }

    const maintenanceStrategyModel = this.formMaintenanceStrategy.getRawValue() as MaintenanceStrategyModel;
    const intervalValid = maintenanceStrategyModel.interval;
    const frequencyValid = this.formMaintenanceStrategy.get("frequency")?.value;

    if (intervalValid == 0 || frequencyValid == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Haz clic en "Agregar" para ingresar un registro válido'
      );
      return;
    }

    maintenanceStrategyModel.articleId = this.equipmentLogbookModel.articleId;
    if (this.maintenanceStrategyContract.interval == 0) {
      this.maintenanceStrategyContract = await firstValueFrom(
        this.maintenanceStrategyService.createMaintenanceStrategy(maintenanceStrategyModel)
      );
      this.validateFormStrategy(this.maintenanceStrategyContract);
      this.addMaintenanceStrategy();
      this.toastHandlerService.showToast(
        'success',
        'La estrategia mtto. se creo exitosamente'
      );
    } else {
      this.maintenanceStrategyContract.maintenancePlan = maintenanceStrategyModel.maintenancePlan;
      this.maintenanceStrategyContract = await firstValueFrom(
        this.maintenanceStrategyService.updateMaintenanceStrategy(this.maintenanceStrategyContract)
      );
      this.validateFormStrategy(this.maintenanceStrategyContract);
      this.toastHandlerService.showToast(
        'success',
        'La hoja de vida se actualizo exitosamente'
      );
    }
    this.getAllMaintenanceStrategiesWithPagination();
    this.maintenanceStrategyContract = new MaintenanceStrategyModel();
    this.validateFormStrategy(this.maintenanceStrategyContract);
  }

  showValidationMessages() {
    const controlMessages: any = {
      unitId: 'El campo unidad es obligatorio.',
      maintenanceFrequency: 'El campo frecuencia mtt. es obligatorio.',
      tolerance: 'El campo Holgura es obligatorio.',
    };
    Object.keys(controlMessages).forEach(control => {
      if (this.formEquipmentLogbook.controls[control].invalid) {
        this.toastHandlerService.showToast('warning', controlMessages[control], 'Atención');
      }
    });
  }

  showValidationMessagesStrategy() {
    const controlMessages: any = {
      interval: {
        required: 'Haz clic en "Agregar" para ingresar un intervalo válido.',
        min: 'Haz clic en "Agregar" para ingresar un intervalo válido.'
      },
      frequency: {
        required: 'Haz clic en "Agregar" para ingresar una frecuencia válida.',
        min: 'Haz clic en "Agregar" para ingresar un frecuencia válida.'
      },
      maintenancePlan: {
        required: 'El plan de mtto es obligatorio.'
      }
    };
    Object.keys(controlMessages).forEach(control => {
      if (this.formMaintenanceStrategy.controls[control].invalid) {
        this.toastHandlerService.showToast('warning', controlMessages[control], 'Atención');
      }
    });
  }

  async updateEquipmentLogbook() {
    if (!this.formEquipmentLogbook.valid) {
      this.showValidationMessages();
      return;
    }
    const equipmentLogbookmodel = this.formEquipmentLogbook.getRawValue() as EquipmentLogbookModel;
    this.equipmentLogbookModel.unitId = equipmentLogbookmodel.unitId;
    this.equipmentLogbookModel.maintenanceFrequency = equipmentLogbookmodel.maintenanceFrequency;
    this.equipmentLogbookModel.tolerance = equipmentLogbookmodel.tolerance;
    this.equipmentLogbookModel = await firstValueFrom(this.equipmentLogbookService.updateArticle(this.equipmentLogbookModel));
    this.validateForm(this.equipmentLogbookModel);
    this.toastHandlerService.showToast(
      'success',
      'La hoja de vida se actualizo exitosamente'
    );
  }

  async validateForm(equipmentLogbook: EquipmentLogbookModel) {
    this.formEquipmentLogbook = this.formBuilder.group({
      unitId: [equipmentLogbook.unitId, [Validators.required, Validators.min(1)]],
      maintenanceFrequency: [equipmentLogbook.maintenanceFrequency, [Validators.required, Validators.min(1)]],
      tolerance: [equipmentLogbook.tolerance, [Validators.required, Validators.min(1)]]
    });
    if (this.defaultDayUnitId && equipmentLogbook.unitId === this.defaultDayUnitId) {
      setTimeout(() => this.formEquipmentLogbook.controls['unitId'].disable());
    }
  }

  validateFormStrategy(maintenanceStategy: MaintenanceStrategyModel) {
    this.formMaintenanceStrategy = this.formBuilder.group({
      interval: [{ value: maintenanceStategy.interval, disabled: true }, [Validators.required, Validators.min(1)]],
      frequency: [{ value: maintenanceStategy.interval * this.equipmentLogbookModel.maintenanceFrequency!, disabled: true }, [Validators.required, Validators.min(1)]],
      maintenancePlan: [maintenanceStategy.maintenancePlan, [Validators.required, Validators.min(1)]],
      articleId: [this.articleId, []]
    });
  }
}
